<template>
  <div class="zone-assignments-field">
    <div class="d-flex align-center flex-wrap" style="gap: 4px">
      <v-progress-circular v-if="loading" indeterminate size="14" width="2" color="primary" />
      <v-chip
        v-for="zone in recordZones"
        :key="zone.assignmentId"
        small
        :close="editable"
        close-icon="mdi-close"
        @click:close="remove(zone)">
        <span class="zone-dot mr-1" :style="{ backgroundColor: zone.colour }"></span>
        {{ zone.name }}
        <span class="zone-type ml-1">· {{ typeName(zone.zoneTypeId) }}</span>
      </v-chip>
      <span v-if="!loading && !recordZones.length" class="empty">No zones</span>

      <!-- Above the Edit Task slide-out (z-index 1997), as the task card's other menus are. -->
      <v-menu
        v-if="editable"
        v-model="menu"
        :close-on-content-click="false"
        :z-index="MENU_Z_INDEX"
        offset-y
        max-width="320">
        <template v-slot:activator="{ on, attrs }">
          <v-btn x-small text color="primary" v-bind="attrs" v-on="on">
            <v-icon x-small left>mdi-plus</v-icon>Add zone
          </v-btn>
        </template>
        <v-card class="pa-2">
          <v-autocomplete
            v-model="toAdd"
            :items="addableItems"
            :menu-props="{ zIndex: MENU_Z_INDEX + 1 }"
            item-text="name"
            item-value="id"
            multiple
            chips
            small-chips
            deletable-chips
            dense
            outlined
            hide-details
            autofocus
            label="Zones"
            placeholder="Search zones" />
          <p v-if="replacesOnAdd" class="caption mt-2 mb-0">
            <v-icon x-small color="warning">mdi-swap-horizontal</v-icon>
            Some of these types allow one zone per record. You'll be asked before a current zone is replaced.
          </p>
          <div class="d-flex justify-end mt-2">
            <v-btn small text @click="menu = false">Cancel</v-btn>
            <v-btn small color="primary" :disabled="!toAdd.length" :loading="saving" @click="add">Add</v-btn>
          </div>
        </v-card>
      </v-menu>
    </div>
    <ZoneReplaceDialog ref="replace" />
  </div>
</template>

<script>
  import ZoneReplaceDialog from './ZoneReplaceDialog.vue';
  import zonesService from '../services/zones.service';
  import { assignZones } from '../utils/zoneAssign';
  import { error } from '@/utils/debugLogger';
  import permissions from '@Share/config/permissions';
  import { $has } from '@/utils';

  const MENU_Z_INDEX = 2100;

  /**
   * Zones on a single tag or task (AVA-764), shown on tag and task cards and in the Zones palette's
   * record rows. A record can be in zones of several types; within one type, in several zones only
   * when the type allows it. For a one-zone-only type the picker takes one zone, and adding it while
   * the record already has a zone of that type asks before replacing (ZoneReplaceDialog). Only active
   * zones of the facility in view are listed or offered. Adding and removing needs Zones.Assign.
   */
  export default {
    name: 'ZoneAssignmentsField',
    components: { ZoneReplaceDialog },
    props: {
      recordType: { type: String, required: true },
      recordId: { type: String, required: true },
      // null = follow the user's Zones.Assign permission.
      canEdit: { type: Boolean, default: null },
    },
    data() {
      return { recordZones: [], loading: false, saving: false, menu: false, toAdd: [], MENU_Z_INDEX };
    },
    computed: {
      editable() {
        return this.canEdit ?? $has(permissions.Zones.Assign);
      },
      allZones() {
        return this.$store.getters['ZONES/GET_ZONES'];
      },
      zoneTypes() {
        return this.$store.getters['ZONES/GET_ZONE_TYPES'];
      },
      addableItems() {
        const assigned = new Set(this.recordZones.map((z) => z.id));
        const items = [];
        this.zoneTypes.forEach((type) => {
          const zones = this.allZones.filter((z) => z.zoneTypeId === type.id && !assigned.has(z.id));
          if (!zones.length) return;
          items.push({ header: type.allowMultipleAssignments ? type.name : `${type.name} · one zone per record` }, ...zones);
        });
        return items;
      },
      // A picked zone of a one-zone-only type the record already has a zone of.
      replacesOnAdd() {
        const held = new Set(this.recordZones.map((z) => z.zoneTypeId));
        return this.toAdd.some((id) => {
          const typeId = this.zoneById(id)?.zoneTypeId;
          return held.has(typeId) && this.isSingle(typeId);
        });
      },
    },
    watch: {
      recordId: { immediate: true, handler: 'load' },
      // One zone per one-zone-only type: picking another zone of that type swaps the earlier pick.
      toAdd(value, previous = []) {
        const added = value.find((id) => !previous.includes(id));
        const typeId = added && this.zoneById(added)?.zoneTypeId;
        if (!typeId || !this.isSingle(typeId)) return;
        const kept = value.filter((id) => id === added || this.zoneById(id)?.zoneTypeId !== typeId);
        if (kept.length !== value.length) this.toAdd = kept;
      },
    },
    methods: {
      typeName(typeId) {
        return this.$store.getters['ZONES/GET_ZONE_TYPE_NAME'](typeId);
      },
      zoneById(id) {
        return this.allZones.find((z) => z.id === id) || null;
      },
      isSingle(typeId) {
        return this.$store.getters['ZONES/GET_ZONE_TYPE'](typeId)?.allowMultipleAssignments === false;
      },
      async load() {
        if (!this.recordId) return;
        this.loading = true;
        // The zones to offer come from the palette's cache, which a card may open before the palette.
        if (this.editable) this.$store.dispatch('ZONES/ENSURE_LOADED').catch((err) => error('Failed to load zones', err));
        try {
          this.recordZones = (await zonesService.getRecordZones(this.recordType, this.recordId)).data;
        } catch (err) {
          error('Failed to load record zones', err);
        } finally {
          this.loading = false;
        }
      },
      async add() {
        this.saving = true;
        try {
          const items = this.toAdd.map((zoneId) => ({ zoneId, recordType: this.recordType, recordId: this.recordId }));
          const result = await assignZones(this.$store, items, (conflicts, options) => this.$refs.replace.ask(conflicts, options));
          if (!result) return; // cancelled: the picks stay so the user can change them
          this.toAdd = [];
          this.menu = false;
          await this.load();
          this.$emit('changed');
        } catch (err) {
          this.$dialog.notify.error(err.message);
        } finally {
          this.saving = false;
        }
      },
      async remove(zone) {
        try {
          await this.$store.dispatch('ZONES/UNASSIGN', [zone.assignmentId]);
          await this.load();
          this.$emit('changed');
        } catch (err) {
          this.$dialog.notify.error(err.message);
        }
      },
    },
  };
</script>

<style scoped lang="scss">
  .zone-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: var(--ds-radius-full);
  }

  .zone-type,
  .empty {
    font-size: var(--ds-font-caption);
    opacity: 0.75;
  }
</style>
